import { useMemo, useReducer, useCallback } from "react";

import { convertToMarkdown } from "@/components/helpers/string-utils";
import {
  ScenarioType,
  ScenarioDifficulty,
  useEditScenarioMutation,
  useUpdateScenarioCharacterMutation,
  ScenarioDocument,
} from "@/graphql";

interface state {
  context?: string;
  objective?: string;
  name?: string;
  description?: string;
  error?: string;
  tab: string;
  difficulty?: ScenarioDifficulty;
  persona: {
    name: string;
    photo?: string;
    voiceId: string;
    openingLine?: string;
    personalityTraits: {
      [key: string]: string;
    };
    personalityCharacteristics?: string[];
    isTraitSelector: boolean;
  };
}

interface action {
  type: string;
  payload?: string;
  initialScenario?: ScenarioType;
  difficulty?: ScenarioDifficulty;
  traits?: {
    [key: string]: string;
  };
  characteristics?: string[];
}
const reducer = (state: state, action: action) => {
  switch (action.type) {
    case "setContext":
      return { ...state, context: action.payload, error: null };
    case "setObjective":
      return { ...state, objective: action.payload, error: null };
    case "setName":
      return { ...state, name: action.payload, error: null };
    case "setError":
      return {
        ...state,
        ...stateFromScenario(action.initialScenario),
        error: action.payload,
        tab: state.tab,
      };
    case "setTab":
      return { ...state, tab: action.payload };
    case "setDescription":
      return {
        ...state,
        description: action.payload,
        error: null,
      };
    case "setDifficulty":
      return { ...state, difficulty: action.difficulty, error: null };
    case "setPersonalityTraits":
      return {
        ...state,
        persona: { ...state.persona, personalityTraits: action.traits },
        error: null,
      };
    case "setVoiceId":
      return {
        ...state,
        persona: { ...state.persona, voiceId: action.payload },
        error: null,
      };
    case "setOpeningLine":
      return {
        ...state,
        persona: { ...state.persona, openingLine: action.payload },
        error: null,
      };
    case "setPersonaName":
      return {
        ...state,
        persona: { ...state.persona, name: action.payload },
        error: null,
      };
    case "setPersonalityCharacteristics":
      return {
        ...state,
        persona: {
          ...state.persona,
          personalityCharacteristics: action.characteristics,
        },
        error: null,
      };
    case "switchToStructured":
      return {
        ...state,
        persona: {
          ...state.persona,
          isTraitSelector: true,
        },
        error: null,
      };
  }
};

function stateFromScenario(scenario: ScenarioType): state {
  return {
    context: scenario?.context ?? "",
    objective: scenario?.objective ?? "",
    name: scenario?.name ?? "",
    description: scenario?.shortDesc ?? "",
    difficulty: scenario?.difficulty ?? ScenarioDifficulty.Custom,
    error: null,
    tab: "basic",
    persona: {
      name: scenario?.personaName ?? "",
      photo: scenario?.personaPhotoUrl ?? "",
      voiceId: scenario?.voiceId ?? "",
      personalityTraits: Object.fromEntries(
        scenario?.personalityTraits?.map((trait) => [
          trait.attributeId,
          trait.levelId,
        ]) ?? [],
      ),
      personalityCharacteristics:
        scenario?.personalityCharacteristics
          ?.split("\n")
          .map((line) => line.replace(/^[-*]\s+/, "").trim()) ?? [],
      isTraitSelector: scenario?.personalityTraits?.length > 0,
      openingLine: scenario?.personaOpeningLine ?? "",
    },
  };
}
export const useEditScenario = (
  scenario: ScenarioType,
  onClose: () => void,
) => {
  const initialState = useMemo(() => {
    return stateFromScenario(scenario);
  }, [scenario]);

  const [state, dispatch] = useReducer(reducer, initialState);

  const [editScenario, { loading }] = useEditScenarioMutation();
  const [updateScenarioCharacter, { loading: updateScenarioCharacterLoading }] =
    useUpdateScenarioCharacterMutation();
  const isDirty = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { tab: _t, ...formState } = state;
    // eslint-disable-next-line no-unused-vars
    const { tab: _t2, ...initialFormState } = initialState;
    return JSON.stringify(formState) !== JSON.stringify(initialFormState);
  }, [state, initialState]);

  const isInvalid = useMemo(() => {
    return (
      state.name === "" ||
      state.context === "<p></p>" ||
      state.objective === "<p></p>" ||
      state.persona.name === "" ||
      state.persona.voiceId === "" ||
      state.persona.openingLine === "" ||
      (Object.keys(state.persona.personalityTraits).length === 0 &&
        state.persona.personalityCharacteristics?.length === 1 &&
        state.persona.personalityCharacteristics?.[0] === "")
    );
  }, [state]);

  const disabled = useMemo(() => {
    return !isDirty || isInvalid;
  }, [isDirty, isInvalid]);

  const handleSave = useCallback(() => {
    const { name, context, objective, description, difficulty, persona } =
      state;
    const {
      name: initialName,
      context: initialContext,
      objective: initialObjective,
      description: initialDescription,
      difficulty: initialDifficulty,
      persona: initialPersona,
    } = initialState;

    let editPromise: Promise<any> = Promise.resolve({
      data: { editScenario: { ok: true } },
    });
    let updateCharacterPromise: Promise<any> = Promise.resolve({
      data: { updateScenarioCharacter: { ok: true } },
    });

    if (
      name !== initialName ||
      context !== initialContext ||
      objective !== initialObjective ||
      description !== initialDescription ||
      difficulty !== initialDifficulty
    ) {
      editPromise = editScenario({
        variables: {
          slug: scenario?.slug,
          name: state.name,
          context: convertToMarkdown(state.context),
          objective: convertToMarkdown(state.objective),
          description: state.description,
          difficulty: state.difficulty,
        },

        refetchQueries: [
          {
            query: ScenarioDocument,
            variables: { scenarioSlug: scenario?.slug },
          },
        ],
      });
    }
    if (JSON.stringify(persona) !== JSON.stringify(initialPersona)) {
      updateCharacterPromise = updateScenarioCharacter({
        variables: {
          slug: scenario?.slug,
          personaName: state.persona.name,
          voiceId: state.persona.voiceId,
          personalityCharacteristics:
            state.persona.personalityCharacteristics?.join("\n- "),
          personalityTraits: Object.entries(
            state.persona.personalityTraits,
          ).map(([attributeId, levelId]) => ({
            attributeId,
            levelId,
          })),
          openingLine: state.persona.openingLine,
        },
        refetchQueries: [
          {
            query: ScenarioDocument,
            variables: { scenarioSlug: scenario?.slug },
          },
        ],
      });
    }

    Promise.all([editPromise, updateCharacterPromise])
      .then(([editResult, updateCharacterResult]) => {
        if (
          editResult.data.editScenario.ok &&
          updateCharacterResult.data.updateScenarioCharacter.ok
        ) {
          onClose();
        } else {
          dispatch({
            type: "setError",
            payload:
              (editResult.data.editScenario.errors?.join(", ") ?? "") +
              (updateCharacterResult.data.updateScenarioCharacter.errors?.join(
                ", ",
              ) ?? ""),
            initialScenario: scenario,
          });
        }
      })
      .catch((error) => {
        dispatch({
          type: "setError",
          payload: error.message,
          initialScenario: scenario,
        });
      });
  }, [state, scenario, editScenario, updateScenarioCharacter, onClose]);

  return {
    state,
    dispatch,
    disabled,
    handleSave,
    loading: updateScenarioCharacterLoading || loading,
  };
};

export type { state, action };
